<template>
  <c-box>
    <c-box
      :box-shadow="{ md: 'base' }"
      :border-radius="{ md: 'base' }"
      :mx="{ base: '-15px', md: '0' }"
      bg="white"
      :px="{ base: 'md', md: '4xl' }"
      :py="{ base: 'xl', md: '2.5rem' }"
      :mt="{ base: 'xl', md: '3rem' }"
      :mb="{ base: '3xl', sm: '4xl' }"
      text-align="center"
      font-size="md"
    >
      <c-heading
        as="h2"
        font-size="lg"
        font-weight="bold"
        bg="brand.blueLightest"
        py="xl"
        :px="{ base: 'xl', md: '4xl' }"
        border-radius="sharper"
        mb="3xl"
      >
        {{ $t("productReturnV2.summary.title") }}
      </c-heading>
      <c-box v-if="shipper === 'CONTACT_CS'">
        <c-text mb="md">
          {{ beautify(String($t("productReturn.summary.contactCs.weWillBeInTouch"))) }}
        </c-text>

        <c-text mb="md">
          {{ beautify(String($t("productReturn.summary.contactCs.howToSeeReturnDataLater"))) }}
        </c-text>
      </c-box>
      <c-box v-else>
        <c-box v-if="proFormaRequired" mb="3xl">
          <c-heading as="h2" font-size="lg" font-weight="bold" mb="md" data-cy="proformaTitle">
            {{ $t("productReturn.summary.proFormaTitle") }}
          </c-heading>
          <c-button
            variation="solid"
            background="brand.beige"
            border-color="brand.beige"
            white-space="initial"
            data-cy="proformaDownloadButton"
            @click="onDownloadProForma"
          >
            {{ $t("productReturn.summary.proFormaDownloadButton") }}
          </c-button>
        </c-box>
        <c-box v-if="shipper === 'MANUAL'">
          <c-heading as="h2" font-size="lg" font-weight="bold" mb="md">
            {{ $t("productReturn.summary.title") }}
          </c-heading>
          <p
            v-html="$t('productReturn.summary.manualShipping.instructionsHtml')"
            v-chakra
            mb="md"
          />
          <p v-html="$t('productReturn.summary.manualShipping.yourReturnIdHtml')" />
          <c-text mb="md">#{{ rmaId?.substring(0, 7) }}</c-text>
          <c-text>
            <b>{{ $t("productReturn.summary.manualShipping.sendPackageTo") }}</b>
          </c-text>
          <c-text user-select="auto !important">
            iLOQ Product Returns<br />
            Yrttipellontie 10<br />
            90230 Oulu<br />
            Finland
          </c-text>
        </c-box>
        <c-box v-else>
          <c-heading as="h2" font-size="lg" font-weight="bold" mb="md">
            {{ $t("productReturn.summary.title") }}
          </c-heading>

          <c-text v-if="shipper === 'DHL'" mb="md">{{
            beautify(String($t("productReturn.summary.textBefore.DHL")))
          }}</c-text>
          <c-text v-else mb="md">{{
            beautify(String($t("productReturn.summary.textBefore")))
          }}</c-text>

          <c-button
            variation="solid"
            background="brand.beige"
            border-color="brand.beige"
            @click="onClick"
            mb="md"
            white-space="initial"
          >
            {{ $t("productReturn.summary.downloadReturnLabel") }}
          </c-button>
          <c-text mb="md">{{ beautify(String($t("productReturn.summary.textAfter"))) }}</c-text>

          <template v-if="hasParcel">
            <RMAParcelTrackingLink :rma-id="String(rmaId)" link-type="button" />
          </template>

          <c-box v-if="shipper === 'DHL'" mt="md">
            <c-text
              font-size="lg"
              v-html="
                $t('productReturn.summary.textAfter.DHLDropOff', { date: plannedShippingDate })
              "
            />
            <c-flex
              :flex-direction="{ base: 'column', md: 'row' }"
              mt="md"
              :justify-content="{ md: 'center' }"
              gap="lg"
            >
              <RMAAddToCalendarButton
                :label="String($t('productReturn.summary.addDateToCalendar'))"
                :title="String($t('productReturn.summary.calendarEventName'))"
                :description="String($t('productReturn.summary.calendarEventDescription'))"
                :date="plannedShippingDate"
                iCalFileName="reminder-drop-off-dhl-parcel"
              />
              <RMADHLLocator
                :label="String($t('productReturn.summary.seeDropOffLocations'))"
                :countryCode="String(countryCode)"
                :zipCode="String(zipCode)"
              />
            </c-flex>
          </c-box>
        </c-box>
      </c-box>
    </c-box>
    <c-flex :flex-direction="{ base: 'column', sm: 'row' }">
      <router-link to="/support/returns">
        <c-button :mr="{ sm: 'xl' }" :mb="{ base: 'md', sm: '0' }" tabindex="-1">
          {{ $t("productReturn.summary.backToSupport") }}
        </c-button>
      </router-link>
      <c-button @click="getDetailedView()">
        {{ $t("productReturnV2.returnDetails.title") }}
      </c-button>
    </c-flex>
  </c-box>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import RMAParcelTrackingLink from "@/views/rma/RMAParcelTrackingLink.vue";
import RMAAddToCalendarButton from "@/views/rma/RMAAddToCalendarButton.vue";
import RMADHLLocator from "@/views/rma/RMADHLLocator.vue";
import { CBox, CButton, CFlex, CHeading, CLink, CText } from "@chakra-ui/vue";

export default defineComponent({
  name: "RMA04Completed",
  props: {
    rmaId: String,
    hasParcel: Boolean,
    shipper: String,
    countryCode: String,
    zipCode: String,
    proFormaRequired: {
      type: Boolean,
      required: true,
    },
    plannedShippingDate: {
      type: String,
      required: true,
    },
  },
  components: {
    RMAParcelTrackingLink,
    RMAAddToCalendarButton,
    RMADHLLocator,
    CHeading,
    CBox,
    CButton,
    CText,
    CFlex,
    CLink,
  },
  methods: {
    onClick() {
      this.$emit("onDownloadReturnLabel");
    },
    getDetailedView() {
      this.$emit("onGetDetailedView");
    },
    onDownloadProForma() {
      this.$emit("onDownloadInternationalForm");
    },
    onDHLLocatorClick() {
      this.$emit("onDHLLocatorClick");
    },
    beautify(text: string): string {
      // replace '->' with '→'
      let t = text.replace(/->/g, "→");
      // make sure ',' is separated by one space after
      t = t.replace(/ *, */g, ", ");
      // make sure '.' is separated by one space after
      t = t.replace(/ *\. */g, ". ");
      // make sure '→' is surrounded by one spaces
      return t.replace(/ *→ */g, " → ").trim();
    },
  },
});
</script>
